import { Form, Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { warningSentences } from "@/lib/on-behalf-warnings"
import { enrolMissionPath, missionPath } from "@/routes"
import type { EnrolOffer } from "@/types/mission-assign"

import {
  ON_BEHALF_ADDRESS_FIELDS,
  type OnBehalfAnswers,
} from "./mission-assign-on-behalf-control"

/**
 * OKS-1042 — the read-only confirm for « Inscrire pour le créateur »
 * (UX Spec §2.3).
 *
 * ⚠️ READ-ONLY (UX UQ3). Every input lives in the control card; this dialog
 * repeats the staged answers as text and carries them as hidden fields. The
 * seven address inputs inside a dialog is a bad 375px screen, and the shipped
 * assign confirm is a summary, not a second form.
 *
 * ⚠️ Base UI `Dialog` at every width — NOT a Drawer. OKS-1037 UQ1: the drawer's
 * swipe capture fights a scrolling summary on touch.
 *
 * ⚠️ Refusals render HERE as an `Alert role="alert"`, never as a toast, and
 * the response is 302 + `inertia: {errors:}` — a 303 drops the bag in Chrome
 * and the refusal vanishes silently.
 *
 * ⚠️ The dialog can UNMOUNT on the 302 response (the mode card re-renders), so
 * the page also renders a fallback for `errors.base`. A refusal with nowhere
 * to land is the OKS-1040 R1 defect: nothing ships and nothing says why.
 */
interface Props {
  missionId: number
  answers: OnBehalfAnswers
  offer?: EnrolOffer | null
  creatorLabel: string
  /** `errors.base` — a refusal code the server already resolved to a string. */
  baseError?: string
  /** True when the refusal was `already_assigned`, which links the mission. */
  alreadyAssigned?: boolean
  disabled: boolean
  testId?: string
}

export function MissionAssignOnBehalfConfirmDialog({
  missionId,
  answers,
  offer,
  creatorLabel,
  baseError,
  alreadyAssigned = false,
  disabled,
  testId = "mission-assign-on-behalf-confirm-dialog",
}: Props) {
  const { t } = useTranslation()

  const product =
    offer?.products.find(
      (p) => p.mission_product_id === answers.missionProductId,
    ) ?? null

  const variant =
    product?.variants.find((v) => v.id === answers.productVariantId) ?? null

  // OKS-1042 F3 — the CHOSEN product's warnings, as the same sentences the
  // control card shows, from the one shared derivation.
  //
  // ⚠️ What this replaced claimed to be "repeated from the control card rather
  // than recomputed" and was neither: it recomputed over the WHOLE offer,
  // never filtered to the chosen product, with stock state absent from the
  // source set, and rendered a bare product name instead of a sentence. On an
  // exhausted « Trousse » it listed a different product and omitted the
  // exhausted warning — on the last screen before an irreversible write.
  const warnings = product ? warningSentences(t, product, offer) : []

  return (
    <Dialog>
      <DialogTrigger
        render={
          <Button disabled={disabled} data-testid="on-behalf-confirm-trigger">
            {t("missions.assign.on_behalf.confirm.cta")}
          </Button>
        }
      />
      <DialogContent data-testid={testId} className="max-sm:h-full">
        <DialogHeader>
          <DialogTitle>
            {t("missions.assign.on_behalf.confirm.title")}
          </DialogTitle>
          <DialogDescription>
            {t("missions.assign.on_behalf.confirm.body", {
              creator: creatorLabel,
              product: product?.name ?? "",
            })}
          </DialogDescription>
        </DialogHeader>

        {baseError && (
          <Alert
            variant="destructive"
            role="alert"
            data-testid="on-behalf-refusal"
          >
            <AlertDescription>
              {baseError}
              {alreadyAssigned && (
                <>
                  {" "}
                  <Link
                    href={missionPath(missionId)}
                    className="underline"
                    data-testid="on-behalf-refusal-link"
                  >
                    {t(
                      "missions.assign.on_behalf.errors.already_assigned_link",
                    )}
                  </Link>
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        <dl className="grid gap-2 text-sm" data-testid="on-behalf-summary">
          <SummaryRow
            label={t("missions.assign.on_behalf.creator_legend")}
            value={creatorLabel}
          />
          <SummaryRow
            label={t("missions.assign.on_behalf.product_legend")}
            value={product?.name ?? ""}
          />
          {variant && (
            <SummaryRow
              label={t("missions.assign.on_behalf.variant_legend")}
              value={variant.title ?? variant.sku ?? ""}
            />
          )}
          <SummaryRow
            label={t("missions.assign.on_behalf.address_legend")}
            value={ON_BEHALF_ADDRESS_FIELDS.map((f) => answers.address[f])
              .filter(Boolean)
              .join(", ")}
          />
        </dl>

        {/* Absent when there are none — never an empty heading (UX §3). */}
        {warnings.length > 0 && (
          <section data-testid="on-behalf-warnings">
            <h3 className="text-sm font-medium">
              {t("missions.assign.on_behalf.warnings_legend")}
            </h3>
            <ul className="text-muted-foreground list-disc pl-5 text-xs">
              {warnings.map((sentence) => (
                <li key={sentence}>{sentence}</li>
              ))}
            </ul>
          </section>
        )}

        <p
          className="text-muted-foreground text-xs"
          data-testid="on-behalf-keeps-control"
        >
          {t("missions.assign.on_behalf.confirm.creator_keeps_control")}
        </p>

        <Form
          action={enrolMissionPath(missionId)}
          method="post"
          data-testid="on-behalf-form"
        >
          {({ processing }) => (
            <>
              <input
                type="hidden"
                name="crew_membership_id"
                value={answers.crewMembershipId ?? ""}
              />
              <input
                type="hidden"
                name="mission_product_id"
                value={answers.missionProductId ?? ""}
              />
              {/* Omitted, not empty — the cleaner wire shape.
                  ⚠️ CORRECTED (LQE measured): this is NOT a server-side
                  distinction. `ChooseProduct#initialize` normalises with
                  `.presence`, so `""` collapses to nil and takes the absent
                  path — both yield `:variant_required`. An earlier comment here
                  claimed they differed; they do not. (A real-but-unoffered id
                  IS different: it survives `.presence` and maps to
                  `:offer_changed`.) The Vitest arm pins this as an EQUIVALENCE,
                  so if `.presence` is ever dropped a distinction appears and
                  the arm reds. */}
              {answers.productVariantId !== null && (
                <input
                  type="hidden"
                  name="product_variant_id"
                  value={answers.productVariantId}
                />
              )}
              {ON_BEHALF_ADDRESS_FIELDS.map((field) => (
                <input
                  key={field}
                  type="hidden"
                  name={field}
                  value={answers.address[field] ?? ""}
                />
              ))}

              <DialogFooter className="max-sm:flex-col-reverse">
                <Button
                  type="submit"
                  disabled={processing}
                  data-testid="on-behalf-submit"
                >
                  {processing
                    ? t("missions.assign.on_behalf.confirm.submitting")
                    : t("missions.assign.on_behalf.confirm.cta")}
                </Button>
              </DialogFooter>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="break-words">{value}</dd>
    </div>
  )
}
